import { useCallback, useState } from 'react';
import { FILE_SIZE_ERROR_MESSAGE, MAX_UPLOAD_SIZE_BYTES } from '~/common/file/constants';
import { api } from '~/trpc-extra/api';

export function useFileUpload(projectId: string) {
  const { mutateAsync: createUploadUrlForTestFile } = api.testFile.createUploadUrlForTestFile.useMutation();
  const { mutateAsync: markFileAsUploaded } = api.testFile.markFileAsUploaded.useMutation();
  const [error, setError] = useState<string | undefined>(undefined);
  const [isUploading, setIsUploading] = useState(false);
  const trpcUtils = api.useUtils();

  const uploadFile = useCallback(
    async (file: File | null) => {
      setError(undefined);
      setIsUploading(false);

      if (!file) {
        return;
      }

      if (file.size >= MAX_UPLOAD_SIZE_BYTES) {
        setError(FILE_SIZE_ERROR_MESSAGE);
        return;
      }

      try {
        const { uploadUrl, id: testFileId } = await createUploadUrlForTestFile({
          name: file.name,
          mimeType: file.type,
          projectId: projectId!,
        });

        setIsUploading(true);

        const response = await fetch(uploadUrl, {
          method: 'PUT',
          headers: {
            'Content-Type': file.type,
          },
          body: file,
        });

        if (!response.ok) {
          throw new Error('Failed to upload file');
        }

        await markFileAsUploaded({
          id: testFileId,
          projectId: projectId!,
        });
      } catch (e) {
        console.error(e);
        setError('Error uploading file');
      } finally {
        await trpcUtils.testFile.listTestFiles.invalidate({ projectId: projectId! });
        setIsUploading(false);
      }
    },
    [createUploadUrlForTestFile, markFileAsUploaded, trpcUtils.testFile.listTestFiles.invalidate],
  );

  return {
    uploadFile,
    isUploading,
    error,
  };
}
